import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  LOAN_MODE_CONTROL,
  LOAN_PALIERS_ENABLED_CONTROL,
  LOAN_PALIER_DIFFERE_CONTROL,
} from './loan-palier.model';
import {
  LoanPalierLike,
  isLoanDiffere,
  palierDiffereIssueMessage,
  palierDurationIssueMessage,
  palierMonthlyPaymentIssueMessage,
  sumPalierDurations,
  toFiniteNumber,
  toPositiveNumber,
  validateLoanPaliers,
} from './loan-palier.utils';

const TOTAL_DURATION_CONTROL = 'durationTotalInMonth';

export function isLoanDiffereSectionActive(control: AbstractControl): boolean {
  const loanGroup = control?.parent as UntypedFormGroup;

  return (
    !!loanGroup &&
    loanGroup.get(LOAN_MODE_CONTROL)?.value === false &&
    loanGroup.get(LOAN_PALIERS_ENABLED_CONTROL)?.value !== true
  );
}

interface PalierControlContext {
  index: number;
  position: number;
  isLast: boolean;
  rowGroup: UntypedFormGroup;
  loanGroup: AbstractControl;
}

function isPaliersEnabled(loanGroup: AbstractControl | null): boolean {
  return loanGroup?.get(LOAN_PALIERS_ENABLED_CONTROL)?.value === true;
}

function resolvePalierContext(
  control: AbstractControl,
): PalierControlContext | null {
  const rowGroup = control?.parent as UntypedFormGroup;
  const array = rowGroup?.parent as UntypedFormArray;
  const loanGroup = array?.parent;

  if (!rowGroup || !array || !loanGroup || !Array.isArray(array.controls)) {
    return null;
  }

  const index = array.controls.indexOf(rowGroup);

  if (index < 0) {
    return null;
  }

  return {
    index,
    position: index + 1,
    isLast: index === array.controls.length - 1,
    rowGroup,
    loanGroup,
  };
}

export class LoanPalierValidators {
  static array(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const loanGroup = control?.parent;

      if (!loanGroup || !isPaliersEnabled(loanGroup)) {
        return null;
      }

      const messages = validateLoanPaliers(
        control.value,
        loanGroup.get(TOTAL_DURATION_CONTROL)?.value,
      )
        .filter(issue => issue.palierIndex === undefined)
        .map(issue => issue.message);

      return messages.length ? { paliers: messages } : null;
    };
  }

  static duration(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const context = resolvePalierContext(control);

      if (!context || !isPaliersEnabled(context.loanGroup)) {
        return null;
      }

      const message = palierDurationIssueMessage(
        control.value,
        context.position,
      );

      return message ? { custom: message } : null;
    };
  }

  static monthlyPayment(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const context = resolvePalierContext(control);

      if (!context || !isPaliersEnabled(context.loanGroup)) {
        return null;
      }

      if (context.isLast) {
        return null;
      }

      if (
        isLoanDiffere(context.rowGroup.get(LOAN_PALIER_DIFFERE_CONTROL)?.value)
      ) {
        return null;
      }

      return toPositiveNumber(control.value) === null
        ? { custom: palierMonthlyPaymentIssueMessage(context.position) }
        : null;
    };
  }

  static differe(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const context = resolvePalierContext(control);

      if (!context || !isPaliersEnabled(context.loanGroup)) {
        return null;
      }

      if (context.index === 0 || !isLoanDiffere(control.value)) {
        return null;
      }

      return { custom: palierDiffereIssueMessage(context.position) };
    };
  }

  static matchExistingPaliersDuration(
    paliers: LoanPalierLike[] | null | undefined,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!Array.isArray(paliers) || !paliers.length) {
        return null;
      }

      const expectedDuration = sumPalierDurations(paliers);
      const currentDuration = toFiniteNumber(control?.value);

      if (currentDuration === null || currentDuration === expectedDuration) {
        return null;
      }

      return {
        custom: `Ce crédit comporte des paliers : sa durée doit rester de ${expectedDuration} mois. Modifiez-la depuis la fiche du crédit.`,
      };
    };
  }
}
